import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, Check, Loader2, Percent } from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import JeaCheckbox from "@/components/jea/JeaCheckbox";
import Skeleton from "@/components/jea/Skeleton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD } from "@/lib/dates";
import { BROKER_META, BROKER_SLUGS_ORDERED, BUNDLE_META } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { usePreviewGate } from "@/hooks/usePreviewGate";
import { cn } from "@/lib/utils";

const ProRateBadge = () => (
  <span
    className="inline-flex px-2 py-[2px] rounded-[4px] text-[12px] font-bold tracking-[0.06em]"
    style={{
      background: "var(--color-accent-bg-15)",
      border: "1px solid var(--color-accent-border-40)",
      color: "var(--color-accent)",
    }}
    data-testid="pro-rate-badge"
  >
    PRO Rate
  </span>
);

/**
 * RunQueryModal — bundle chips + per-broker checkboxes + real-time cost preview.
 * On submit, POSTs to /debtors/:id/queries and invokes `onCompleted(result)` with
 * the { query_run, report } payload.
 */
const RunQueryModal = ({ open, debtor, onClose, onCompleted }) => {
  const { user } = useAuth();
  const { inPreview, openPaywall } = usePreviewGate();
  const isPro = user?.account_type === "pro";
  const [bundles, setBundles] = useState([]);
  const [pickedBundle, setPickedBundle] = useState("full_asset_sweep");
  const [customMode, setCustomMode] = useState(false);
  const [customSlugs, setCustomSlugs] = useState([...BROKER_SLUGS_ORDERED]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [disabledSlugs, setDisabledSlugs] = useState(new Set());

  // Load bundles + broker enabled state once per open
  useEffect(() => {
    if (!open) return;
    // Preview-mode short-circuit: bail immediately and open the paywall.
    if (inPreview) {
      openPaywall("running queries");
      onClose?.();
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const [bundleRes, brokerRes] = await Promise.all([
          apiClient.get("/queries/bundles"),
          apiClient.get("/brokers"),
        ]);
        if (!mounted) return;
        setBundles(bundleRes.data.bundles || []);
        const disabled = new Set(
          (brokerRes.data.brokers || [])
            .filter((b) => b.globally_enabled === false)
            .map((b) => b.slug)
        );
        setDisabledSlugs(disabled);
        // Drop disabled brokers from custom selection
        setCustomSlugs((prev) => prev.filter((s) => !disabled.has(s)));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    return () => { mounted = false; };
  }, [open]);

  // Reset picks on open
  useEffect(() => {
    if (open) {
      setPickedBundle("full_asset_sweep");
      setCustomMode(false);
      setCustomSlugs([...BROKER_SLUGS_ORDERED]);
    }
  }, [open]);

  // Fetch cost preview whenever selection changes
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setPreviewing(true);
    const body = customMode
      ? { broker_slugs: customSlugs }
      : { bundle: pickedBundle };
    if (customMode && customSlugs.length === 0) {
      setPreview({
        broker_slugs: [], subtotal_cents: 0, discount_cents: 0,
        total_cost_cents: 0, discount_pct: 0, line_items: [],
      });
      setPreviewing(false);
      return () => { alive = false; };
    }
    (async () => {
      try {
        const { data } = await apiClient.post("/queries/preview-cost", body);
        if (alive) setPreview(data);
      } catch (e) {
        if (alive) toast.error(formatApiError(e));
      } finally {
        if (alive) setPreviewing(false);
      }
    })();
    return () => { alive = false; };
  }, [customMode, pickedBundle, customSlugs, open]);

  const bundleByKey = useMemo(() => {
    const map = {};
    (bundles || []).forEach((b) => (map[b.key] = b));
    return map;
  }, [bundles]);

  const toggleSlug = (slug) => {
    setCustomSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const submit = async () => {
    if (!debtor) return;
    setRunning(true);
    const body = customMode
      ? { broker_slugs: customSlugs }
      : { bundle: pickedBundle };
    try {
      const { data } = await apiClient.post(`/debtors/${debtor.id}/queries`, body);
      toast.success("Query complete — report generated.");
      onCompleted?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const totalBrokers = customMode ? customSlugs.length : (bundleByKey[pickedBundle]?.broker_slugs?.length ?? 0);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={running ? undefined : onClose}
          />
          <motion.div
            className="fixed inset-0 z-[96] flex items-center justify-center px-4 py-6"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            data-testid="run-query-modal"
          >
            <div
              className="w-full max-w-[760px] max-h-[92vh] overflow-y-auto bg-bg-secondary border border-border-default rounded-[16px] p-10 shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="jea-caption">Broker Query</div>
                  <h2 className="text-white font-bold text-[22px]">
                    Run Query for <span className="text-accent-pink">{debtor?.full_name || "Debtor"}</span>
                  </h2>
                  <p className="text-text-secondary text-[13px] mt-1">
                    Fan out to selected brokers in parallel. Results generate a report automatically.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={running}
                  className="p-2 text-text-secondary hover:text-white disabled:opacity-40"
                  data-testid="run-query-close"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Bundle picker */}
              <div className="mb-6" data-testid="run-query-bundles">
                <div className="flex items-center justify-between mb-2">
                  <div className="jea-caption">Bundles</div>
                  <button
                    type="button"
                    onClick={() => setCustomMode((v) => !v)}
                    className={cn(
                      "text-[12px] font-semibold h-8 px-3 rounded-full border transition-all",
                      customMode
                        ? "border-accent-pink text-accent-pink bg-accent-pink/10"
                        : "border-border-default text-text-secondary hover:text-white"
                    )}
                    data-testid="run-query-toggle-custom"
                  >
                    {customMode ? "Using custom brokers" : "Pick brokers manually"}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {bundles.map((b) => {
                    const active = !customMode && pickedBundle === b.key;
                    const meta = BUNDLE_META[b.key] || {};
                    const Icon = meta.Icon;
                    const excludedBrokers = (b.excluded_broker_slugs || []).map((slug) => BROKER_META[slug]?.label || slug);
                    return (
                      <button
                        key={b.key}
                        type="button"
                        disabled={customMode}
                        onClick={() => setPickedBundle(b.key)}
                        className={cn(
                          "text-left rounded-[12px] border p-4 transition-all",
                          active
                            ? "border-accent-pink bg-accent-pink/10 shadow-pink-glow"
                            : "border-border-default bg-bg-primary hover:border-accent-pink/40 hover:bg-bg-tertiary",
                          customMode && "opacity-50 pointer-events-none"
                        )}
                        data-testid={`run-query-bundle-${b.key}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            {Icon ? <Icon size={16} className="text-accent-pink" /> : null}
                            <span className="text-white font-semibold text-[14px]">{b.label}</span>
                          </div>
                          {b.discount_pct > 0 && (
                            <span className="inline-flex items-center gap-1 text-accent-pink text-[11px] font-bold tracking-[0.08em]">
                              <Percent size={11} /> {b.discount_pct}% OFF
                            </span>
                          )}
                        </div>
                        <div className="text-text-secondary text-[12px] mt-2 leading-4">{b.description}</div>
                        <div className="text-text-muted text-[11px] mt-2">
                          {b.broker_slugs?.length ?? 0} broker(s)
                          {excludedBrokers.length > 0 && (
                            <span className="text-warning ml-1">
                              · {excludedBrokers.length} unavailable
                            </span>
                          )}
                        </div>
                        {excludedBrokers.length > 0 && (
                          <div
                            className="mt-2 inline-flex items-center px-2 py-[3px] rounded-[4px] text-[10px] font-semibold text-warning bg-warning/10 border border-warning/40"
                            data-testid={`run-query-bundle-excluded-${b.key}`}
                          >
                            {excludedBrokers.join(", ")} temporarily unavailable
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom broker checklist */}
              {customMode && (
                <div className="mb-6 space-y-2" data-testid="run-query-custom-list">
                  <div className="jea-caption">Brokers</div>
                  {BROKER_SLUGS_ORDERED.map((slug) => {
                    const m = BROKER_META[slug];
                    const Icon = m.Icon;
                    const checked = customSlugs.includes(slug);
                    const disabled = disabledSlugs.has(slug);
                    return (
                      <label
                        key={slug}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-[8px] border transition-all",
                          disabled
                            ? "border-border-default bg-bg-primary opacity-40 cursor-not-allowed"
                            : checked
                              ? "border-accent-pink/60 bg-accent-pink/5 cursor-pointer"
                              : "border-border-default bg-bg-primary hover:bg-bg-tertiary cursor-pointer"
                        )}
                        data-testid={`run-query-broker-${slug}`}
                      >
                        <JeaCheckbox
                          checked={checked && !disabled}
                          disabled={disabled}
                          onChange={() => !disabled && toggleSlug(slug)}
                          data-testid={`run-query-broker-checkbox-${slug}`}
                        />
                        <Icon size={16} className="text-text-secondary shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-[13px] font-semibold flex items-center gap-2">
                            {m.label}
                            {disabled && (
                              <span
                                className="inline-flex px-2 py-[2px] rounded-full text-[10px] font-semibold bg-warning/10 text-warning border border-warning/40"
                                data-testid={`run-query-broker-unavailable-${slug}`}
                              >
                                Temporarily Unavailable
                              </span>
                            )}
                          </div>
                          <div className="text-text-secondary text-[12px]">{m.tagline}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isPro && preview?.line_items?.find((l) => l.slug === slug) && !disabled && (
                            <ProRateBadge />
                          )}
                          <div className={cn(
                            "jea-mono text-[14px] font-bold",
                            disabled ? "text-text-muted" : isPro ? "text-accent-pink" : "text-white",
                          )}>
                            {preview?.line_items?.find((l) => l.slug === slug)
                              ? centsToUSD(preview.line_items.find((l) => l.slug === slug).price_cents)
                              : "—"}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Cost summary */}
              <div className="bg-bg-primary border border-border-default rounded-[12px] p-5" data-testid="run-query-cost-summary">
                <div className="jea-caption mb-3">Cost Summary</div>
                {previewing || !preview ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-4 w-1/3" />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-text-secondary">Subtotal · {totalBrokers} broker(s)</span>
                      <span
                        className={cn(
                          "jea-mono",
                          preview.discount_cents > 0
                            ? "text-text-muted line-through decoration-text-muted"
                            : "text-white"
                        )}
                        data-testid="run-query-subtotal"
                      >
                        {centsToUSD(preview.subtotal_cents)}
                      </span>
                    </div>
                    {preview.discount_cents > 0 && (
                      <div
                        className="flex items-center justify-between text-[13px] mt-1"
                        data-testid="run-query-discount-line"
                      >
                        <span className="text-success">
                          Bundle discount · {preview.discount_pct}% off
                        </span>
                        <span className="jea-mono text-success">
                          −{centsToUSD(preview.discount_cents)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border-default">
                      <span className="text-text-secondary text-[14px]">Total</span>
                      <span
                        className="jea-mono text-accent-pink font-bold text-[18px]"
                        data-testid="run-query-total"
                      >
                        {centsToUSD(preview.total_cost_cents)}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={running}
                  className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[14px] font-semibold transition-colors disabled:opacity-40"
                  data-testid="run-query-cancel"
                >
                  Cancel
                </button>
                <JeaButton
                  onClick={submit}
                  loading={running}
                  disabled={totalBrokers === 0}
                  className="gap-2"
                  data-testid="run-query-submit"
                >
                  {running ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Running Query…
                    </>
                  ) : (
                    <>
                      <Play size={14} /> Run Query · {centsToUSD(preview?.total_cost_cents || 0)}
                    </>
                  )}
                </JeaButton>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default RunQueryModal;
